import { ArtistsCollections, ContentNotFound, Layout } from '@common'
import { ISSRPageData } from '@interfaces'
import { ArtistService, HeadlineService } from '@services'
import useTranslation from 'next-translate/useTranslation'
import { useRouter } from 'next/router'
import { useState, useMemo } from 'react'
import {
  Box,
  Input,
  InputGroup,
  InputLeftElement,
  Container,
  Text,
  Flex,
  useBreakpointValue,
} from '@chakra-ui/react'
import { SearchIcon } from '@chakra-ui/icons'

import { Swiper, SwiperSlide } from 'swiper/react'
import SwiperCore, { Autoplay, EffectFade } from 'swiper'
import { motion, AnimatePresence } from 'framer-motion'

SwiperCore.use([Autoplay, EffectFade])

const ArtistsPage = ({ pageData, global: { navigation } }: ISSRPageData) => {
  const { asPath } = useRouter()
  const { t } = useTranslation()
  const [searchTerm, setSearchTerm] = useState('')
  const [activeSlide, setActiveSlide] = useState(0)

  if (pageData.error) {
    return (
      <Layout title={t('common:artists')} navigation={navigation} path={asPath}>
        <ContentNotFound infoType="serverSideError" />
      </Layout>
    )
  }

  const { collection } = pageData.data.artists

  // Arama fonksiyonu
  const filteredCollection = useMemo(() => {
    if (!searchTerm.trim()) {
      return collection
    }

    const searchLower = searchTerm.toLowerCase().trim()

    return collection.filter((artist) => {
      const fullNameMatch = artist.fullName.toLowerCase().includes(searchLower)
      const lifeRangeMatch =
        artist.lifeRange && artist.lifeRange.toLowerCase().includes(searchLower)

      return fullNameMatch || lifeRangeMatch
    })
  }, [collection, searchTerm])

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value)
  }

  const placeholderText = useBreakpointValue({
    base: t('common:artistsSearch.mobileSearchLabel'),
    lg: t('common:artistsSearch.searchLabel'),
  })

  return (
    <Layout title={t('common:artists')} navigation={navigation} path={asPath}>
      {pageData?.data?.headline?.length > 0 && (
        <Box
          h={{ base: '30vh', lg: '80vh' }}
          position="relative"
          overflow="hidden"
        >
          <Swiper
            effect="fade"
            fadeEffect={{
              crossFade: true,
            }}
            autoplay={{
              delay: 5000,
              disableOnInteraction: false,
            }}
            speed={2000}
            loop={true}
            onSlideChange={(swiper) => setActiveSlide(swiper.realIndex)}
            className="hero-swiper"
            style={{
              height: '100%',
              width: '100%',
            }}
          >
            {pageData.data.headline.map((item, index) => (
              <SwiperSlide key={index}>
                <AnimatePresence>
                  {activeSlide === index && (
                    <motion.div
                      key={`slide-${index}`}
                      initial={{
                        scale: 1.1,
                        opacity: 1,
                      }}
                      animate={{
                        scale: 1,
                        opacity: 1,
                      }}
                      exit={{ scale: 1, opacity: 0.7 }}
                      transition={{
                        duration: 12,
                        ease: [0.25, 0.25, 0.25, 1],
                      }}
                      style={{
                        backgroundImage: `linear-gradient(rgba(0,0,0,0.2), rgba(0,0,0,0.3)), url(${item.imgUrl})`,
                        backgroundSize: 'cover',
                        backgroundPosition:'center',
                        height: '100%',
                        width: '100%',
                        display: 'flex',
                        position: 'relative',
                      }}
                    ></motion.div>
                  )}
                </AnimatePresence>
              </SwiperSlide>
            ))}
          </Swiper>
        </Box>
      )}

      {/* Arama Alanı */}
      <Container
        maxWidth="container.xl"
        px={{ base: 'padding.primary', lg: 'padding.primary4' }}
        py={4}
        my="4"
        bg="#faf7f0"
      >
        <Box mx="auto">
          <InputGroup size="md">
            <InputLeftElement pointerEvents="none">
              <SearchIcon color="blackAlpha.primary10" mt="2" />
            </InputLeftElement>
            <Input
              placeholder={placeholderText}
              value={searchTerm}
              onChange={handleSearchChange}
              size="lg"
              bg="white"
              fontSize="md"
              border="2px solid #dcdcdc"
              _hover={{
                borderColor: 'red.primary',
              }}
              _focus={{
                borderColor: 'red.primary',
              }}
              borderRadius="sm"
            />
          </InputGroup>

          {/* Arama Sonucu Bilgisi */}
          {searchTerm && (
            <Flex justify="space-between" align="center" mt={3}>
              <Text fontSize="sm" color="black.primary">
                {filteredCollection.length}{' '}
                {t('common:artistsSearch.findArtist')}
              </Text>
              {searchTerm && (
                <Text
                  fontSize="sm"
                  color="red.primary"
                  cursor="pointer"
                  onClick={() => setSearchTerm('')}
                  _hover={{ textDecoration: 'underline' }}
                >
                  {t('common:artistsSearch.clear')}
                </Text>
              )}
            </Flex>
          )}
        </Box>
      </Container>

      {/* Sanatçı Koleksiyonu */}
      {filteredCollection.length > 0 ? (
        <ArtistsCollections
          collection={filteredCollection}
          containerProps={{
            pt: { base: '2', lg: '4' },
          }}
        />
      ) : (
        <Container
          maxWidth="container.xl"
          px={{ base: 'padding.primary', lg: '0' }}
          py="10"
        >
          <Box textAlign="center" py={10}>
            <Text fontSize="lg" color="gray.600" mb={2}>
              {t('common:artistsSearch.notFound')}
            </Text>
            <Text fontSize="sm" color="gray.500">
              {t('common:artistsSearch.diffSearch')}
            </Text>
          </Box>
        </Container>
      )}
    </Layout>
  )
}

export default ArtistsPage

export async function getStaticProps({ locale }) {
  const pageData = {
    data: {
      artists: {
        collection: null,
        totalCount: 0,
      },
      headline: null,
    },
    error: null,
  }

  try {
    const [allArtists, headlineData] = await Promise.all(
      [
        ArtistService.getAllArtists(locale),
        HeadlineService.getAllHeadline(locale, 1),
      ]
    )

    pageData.data.artists.collection = allArtists
    pageData.data.artists.totalCount = allArtists.length
    pageData.data.headline = headlineData
    //console.log(" Artists:", JSON.stringify(pageData.data.artists.collection, null, 2))
  } catch (err) {
    if (err instanceof Error) {
      pageData.error = { message: err.message }
    }
  }

  return {
    props: {
      pageData,
    },
  }
}
